<template>
  <div class="container-fluid roles-page">
    <Breadcrumbs title="Roles" main="Roles" />
    <div class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h5 class="card-title mb-1">Roles y permisos</h5>
            <p class="text-muted mb-0">
              Define a qué opciones del menú tiene acceso cada rol. Los cambios aplican a todos los usuarios con ese rol.
            </p>
          </div>
          <button type="button" class="btn btn-primary d-flex align-items-center gap-2" @click="abrirEditor(null)">
            <vue-feather type="plus" size="18" />
            Nuevo rol
          </button>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead>
              <tr>
                <th>Rol</th>
                <th class="text-center">Usuarios</th>
                <th>Acceso</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="4" class="text-center py-4">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Cargando roles...
                </td>
              </tr>
              <tr v-else-if="!rolesDetalle.length">
                <td colspan="4" class="text-center text-muted py-4">No hay roles registrados.</td>
              </tr>
              <template v-else>
              <tr v-for="rol in rolesPagina" :key="rol.id">
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="rol-icono" :style="{ backgroundColor: colorRol(rol.id) }">
                      <vue-feather type="shield" size="16" />
                    </span>
                    <span class="fw-semibold">{{ rol.name }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="badge rounded-pill bg-light text-dark px-3 py-2">{{ rol.totalUsuarios }}</span>
                </td>
                <td>
                  <span v-if="rol.esAdmin" class="badge bg-warning text-dark px-3 py-2">Acceso total</span>
                  <template v-else>
                    <div class="small text-muted mb-1">
                      {{ permisosVigentes(rol).length }} de {{ totalOpciones }} opciones
                    </div>
                    <div class="progress acceso-progress">
                      <div
                        class="progress-bar bg-success"
                        :style="{ width: porcentajeAcceso(rol) + '%' }"
                      ></div>
                    </div>
                  </template>
                </td>
                <td>
                  <div class="d-flex gap-2 justify-content-center flex-wrap">
                    <button type="button" class="btn btn-success btn-action text-white" title="Editar nombre"
                      @click="editarNombre(rol)">
                      <vue-feather type="edit" size="20" />
                      <span class="btn-action-label">Editar</span>
                    </button>
                    <button type="button" class="btn btn-primary btn-action text-white" title="Editar permisos"
                      @click="abrirEditor(rol)">
                      <vue-feather type="lock" size="20" />
                      <span class="btn-action-label">Permisos</span>
                    </button>
                    <button type="button" class="btn btn-info btn-action text-white" title="Ver usuarios con este rol"
                      @click="verUsuarios(rol)">
                      <vue-feather type="users" size="20" />
                      <span class="btn-action-label">Usuarios</span>
                    </button>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
        <PaginacionTabla v-if="!cargando" :pagina="pagina" :total-paginas="totalPaginas" @cambiar="pagina = $event" />
      </div>
    </div>

    <!-- Alta de rol / edición de permisos -->
    <div v-if="editor.visible" class="roles-modal">
      <div class="modal-backdrop fade show" @click="cerrarEditor"></div>
      <div class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ editor.id ? `Permisos del rol: ${editor.nombreOriginal}` : "Nuevo rol" }}
              </h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarEditor"></button>
            </div>
            <div class="modal-body">
              <div v-if="!editor.id" class="mb-4">
                <label class="form-label fw-semibold">Nombre del rol</label>
                <input
                  v-model="editor.nombre"
                  type="text"
                  maxlength="50"
                  class="form-control"
                  :class="{ 'is-invalid': editor.errorNombre }"
                  placeholder="Ej. Diseño"
                />
                <div v-if="editor.errorNombre" class="invalid-feedback d-block">{{ editor.errorNombre }}</div>
              </div>

              <div v-if="editor.esAdmin" class="alert alert-warning d-flex align-items-center gap-2 py-2">
                <vue-feather type="info" size="18" />
                El rol Administrador siempre tiene acceso total; sus permisos no se pueden modificar.
              </div>

              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <span class="small text-muted">
                  {{ editor.esAdmin ? totalOpciones : editor.permisos.length }} de {{ totalOpciones }} opciones con acceso
                </span>
                <div v-if="!editor.esAdmin" class="d-flex gap-2">
                  <button type="button" class="btn btn-outline-success btn-sm" @click="seleccionarTodo(true)">
                    Seleccionar todo
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="seleccionarTodo(false)">
                    Quitar todo
                  </button>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-sm align-middle permisos-tabla mb-2">
                  <thead>
                    <tr>
                      <th>Opción del menú</th>
                      <th class="text-center acceso-col">Acceso</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="modulo in modulos" :key="modulo.titulo">
                      <tr class="modulo-fila">
                        <td>
                          <div class="d-flex align-items-center gap-2 fw-semibold">
                            <vue-feather :type="modulo.icono" size="16" />
                            {{ modulo.titulo }}
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="form-check form-switch d-inline-block m-0">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              role="switch"
                              :checked="moduloCompleto(modulo)"
                              :indeterminate.prop="moduloParcial(modulo)"
                              :disabled="editor.esAdmin"
                              :title="esSubmenu(modulo) ? 'Todo el módulo' : 'Acceso'"
                              @change="alternarModulo(modulo, $event.target.checked)"
                            />
                          </div>
                        </td>
                      </tr>
                      <template v-if="esSubmenu(modulo)">
                        <tr v-for="opcion in modulo.opciones" :key="opcion.path">
                          <td class="ps-5 text-muted">{{ opcion.titulo }}</td>
                          <td class="text-center">
                            <div class="form-check form-switch d-inline-block m-0">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                role="switch"
                                :checked="tienePermiso(opcion.path)"
                                :disabled="editor.esAdmin"
                                @change="alternarPermiso(opcion.path, $event.target.checked)"
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
              <p class="small text-muted mb-0">
                <vue-feather type="info" size="14" class="me-1" />
                Mi Perfil y Cambiar contraseña están disponibles para todos los usuarios.
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" @click="cerrarEditor">Cancelar</button>
              <button
                v-if="!editor.esAdmin"
                type="button"
                class="btn btn-primary d-flex align-items-center gap-2"
                :disabled="editor.guardando"
                @click="guardarEditor"
              >
                <span v-if="editor.guardando" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <vue-feather v-else type="check" size="16" />
                {{ editor.id ? "Guardar permisos" : "Crear rol" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Usuarios con el rol -->
    <div v-if="usuariosModal.visible" class="roles-modal">
      <div class="modal-backdrop fade show" @click="usuariosModal.visible = false"></div>
      <div class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Usuarios con el rol: {{ usuariosModal.rol }}</h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="usuariosModal.visible = false"></button>
            </div>
            <div class="modal-body">
              <div v-if="usuariosModal.cargando" class="text-center py-4">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cargando usuarios...
              </div>
              <div v-else-if="!usuariosModal.lista.length" class="text-center text-muted py-4">
                Ningún usuario tiene asignado este rol.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-striped align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Correo</th>
                      <th class="text-center">Estatus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="usuario in usuariosModal.lista" :key="usuario.id">
                      <td>{{ usuario.name }}</td>
                      <td>{{ usuario.email }}</td>
                      <td class="text-center">
                        <span
                          class="badge py-2 px-3"
                          :class="usuario.status === 'active' ? 'bg-success' : 'bg-danger'"
                        >
                          {{ usuario.status === "active" ? "Activo" : "Inactivo" }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <span class="me-auto small text-muted">{{ usuariosModal.lista.length }} usuario(s)</span>
              <button type="button" class="btn btn-outline-secondary" @click="usuariosModal.visible = false">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { modulosConfigurables } from "@/utils/permisos";
import { colorRol } from "@/constants/roles";
import PaginacionTabla from "@/components/PaginacionTabla.vue";

const ROLES_POR_PAGINA = 10;

function validarNombre(nombre) {
  const limpio = (nombre || "").trim();
  if (limpio.length < 2 || limpio.length > 50) return "El nombre debe tener entre 2 y 50 caracteres";
  return "";
}

function editorVacio() {
  return {
    visible: false,
    id: null,
    nombre: "",
    nombreOriginal: "",
    errorNombre: "",
    esAdmin: false,
    permisos: [],
    guardando: false,
  };
}

export default {
  name: "Roles",
  components: { PaginacionTabla },
  data() {
    return {
      cargando: false,
      pagina: 1,
      modulos: modulosConfigurables(),
      editor: editorVacio(),
      usuariosModal: { visible: false, rol: "", lista: [], cargando: false },
    };
  },
  computed: {
    ...mapState("roles", ["rolesDetalle"]),
    rutasMenu() {
      return this.modulos.flatMap((m) => m.opciones.map((o) => o.path));
    },
    totalOpciones() {
      return this.rutasMenu.length;
    },
    totalPaginas() {
      return Math.ceil(this.rolesDetalle.length / ROLES_POR_PAGINA);
    },
    rolesPagina() {
      const inicio = (this.pagina - 1) * ROLES_POR_PAGINA;
      return this.rolesDetalle.slice(inicio, inicio + ROLES_POR_PAGINA);
    },
  },
  watch: {
    totalPaginas(total) {
      if (this.pagina > total) this.pagina = Math.max(total, 1);
    },
  },
  mounted() {
    this.cargarRoles();
  },
  methods: {
    ...mapActions("roles", ["consultarRolesDetalle", "guardarRol", "consultarUsuariosRol"]),
    colorRol,
    async cargarRoles() {
      this.cargando = true;
      try {
        await this.consultarRolesDetalle();
      } catch (e) {
        Swal.fire({ icon: "error", title: e.code || "Error", text: e.msg || "No se pudieron cargar los roles." });
      } finally {
        this.cargando = false;
      }
    },
    /** Permisos del rol que siguen existiendo en el menú. */
    permisosVigentes(rol) {
      return (rol.permisos || []).filter((p) => this.rutasMenu.includes(p));
    },
    porcentajeAcceso(rol) {
      if (!this.totalOpciones) return 0;
      return Math.round((this.permisosVigentes(rol).length / this.totalOpciones) * 100);
    },
    esSubmenu(modulo) {
      return modulo.opciones.length > 1 || modulo.opciones[0]?.titulo !== modulo.titulo;
    },
    tienePermiso(path) {
      return this.editor.esAdmin || this.editor.permisos.includes(path);
    },
    moduloCompleto(modulo) {
      return modulo.opciones.every((o) => this.tienePermiso(o.path));
    },
    moduloParcial(modulo) {
      return !this.moduloCompleto(modulo) && modulo.opciones.some((o) => this.tienePermiso(o.path));
    },
    alternarPermiso(path, activo) {
      const sinPath = this.editor.permisos.filter((p) => p !== path);
      this.editor.permisos = activo ? [...sinPath, path] : sinPath;
    },
    alternarModulo(modulo, activo) {
      modulo.opciones.forEach((o) => this.alternarPermiso(o.path, activo));
    },
    seleccionarTodo(activo) {
      this.editor.permisos = activo ? [...this.rutasMenu] : [];
    },
    abrirEditor(rol) {
      this.editor = {
        ...editorVacio(),
        visible: true,
        id: rol?.id ?? null,
        nombre: rol?.name ?? "",
        nombreOriginal: rol?.name ?? "",
        esAdmin: !!rol?.esAdmin,
        permisos: rol ? this.permisosVigentes(rol) : [],
      };
    },
    cerrarEditor() {
      if (this.editor.guardando) return;
      this.editor = editorVacio();
    },
    async guardarEditor() {
      if (!this.editor.id) {
        this.editor.errorNombre = validarNombre(this.editor.nombre);
        if (this.editor.errorNombre) return;
      }
      if (!this.editor.permisos.length) {
        const confirmar = await Swal.fire({
          icon: "warning",
          title: "Rol sin accesos",
          text: "Los usuarios con este rol solo podrán entrar a Mi Perfil. ¿Deseas continuar?",
          showCancelButton: true,
          confirmButtonText: "Sí, continuar",
          cancelButtonText: "Cancelar",
        });
        if (!confirmar.isConfirmed) return;
      }

      this.editor.guardando = true;
      try {
        const esNuevo = !this.editor.id;
        await this.guardarRol({
          id: this.editor.id,
          nombre: esNuevo ? this.editor.nombre.trim() : this.editor.nombreOriginal,
          permisos: this.editor.permisos,
        });
        this.editor.guardando = false;
        this.cerrarEditor();
        Swal.fire({
          icon: "success",
          title: esNuevo ? "Rol creado" : "Permisos actualizados",
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (e) {
        this.editor.guardando = false;
        Swal.fire({ icon: "error", title: e.code || "Error", text: e.msg || "No se pudo guardar el rol." });
      }
    },
    async editarNombre(rol) {
      const { isConfirmed, value } = await Swal.fire({
        title: "Editar nombre del rol",
        input: "text",
        inputValue: rol.name,
        inputAttributes: { maxlength: "50" },
        showCancelButton: true,
        confirmButtonText: "Guardar",
        cancelButtonText: "Cancelar",
        inputValidator: (valor) => validarNombre(valor) || undefined,
      });
      if (!isConfirmed || value.trim() === rol.name) return;
      try {
        await this.guardarRol({ id: rol.id, nombre: value.trim() });
        Swal.fire({ icon: "success", title: "Nombre actualizado", timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: "error", title: e.code || "Error", text: e.msg || "No se pudo actualizar el nombre." });
      }
    },
    async verUsuarios(rol) {
      this.usuariosModal = { visible: true, rol: rol.name, lista: [], cargando: true };
      try {
        this.usuariosModal.lista = await this.consultarUsuariosRol(rol.id);
      } catch (e) {
        this.usuariosModal.visible = false;
        Swal.fire({ icon: "error", title: e.code || "Error", text: e.msg || "No se pudieron cargar los usuarios." });
      } finally {
        this.usuariosModal.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
.roles-modal .modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999990;
}

.roles-modal .modal {
  position: fixed;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 999991;
}

.rol-icono {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.acceso-progress {
  height: 6px;
  max-width: 220px;
}

.permisos-tabla .modulo-fila td {
  background-color: #f8f9fa;
}

.permisos-tabla .acceso-col {
  width: 110px;
}

.permisos-tabla .form-switch .form-check-input {
  width: 2.5em;
  height: 1.25em;
  cursor: pointer;
}

.btn-action {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 92px;
  padding: 0.45rem 0.9rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.1;
  border: none;
  border-radius: 30px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.15);
  opacity: 0.95;
}

.btn-action:active {
  transform: translateY(0);
}

.btn-action-label {
  white-space: nowrap;
  font-size: 10px;
}

@media (max-width: 1200px) {
  .btn-action-label {
    display: none;
  }

  .btn-action {
    min-width: 0;
    padding: 0.5rem;
  }
}
</style>
